import * as Yup from "yup";
import { useFormik } from "formik";
import { MuiOtpInput } from "mui-one-time-password-input";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  FormHelperText,
  FormLabel,
  Link,
  Stack,
  TextField,
  Alert,
} from "@mui/material";
import { RouterLink } from "src/components/router-link";
import { Seo } from "src/components/seo";
import { useAuth } from "src/hooks/use-auth";
import { useMounted } from "src/hooks/use-mounted";
import { usePageView } from "src/hooks/use-page-view";
import { useRouter } from "src/hooks/use-router";
import { useSearchParams } from "src/hooks/use-search-params";
import { paths } from "src/paths";
import { useParams } from "react-router-dom";
import { useEffect, useContext } from "react";
import axios from "axios";
import deployEnvironment from "src/deployEnvironment";
import { useNavigate } from "react-router-dom";
import { Layout as AuthLayout } from "src/layouts/auth/classic-layout";
import { AlertContext } from "../../../components/alert/AlertContext";
import {
  CustomButton,
  CustomTextField,
  CustomTypography,
} from "src/components/inputs";

const API = deployEnvironment;

const getInitialValues = () => ({
  firstname: "",
  lastname: "",
  email: "",
  password: "",
  passwordConfirm: "",
  submit: null,
});

const validationSchema = Yup.object({
  firstname: Yup.string().required("Required"),
  lastname: Yup.string().required("Required"),
  email: Yup.string().email("Invalid email address").required("Required"),
  password: Yup.string()
    .min(7, "Must be at least 7 characters")
    .max(255)
    .required("Required"),
  passwordConfirm: Yup.string()
    .oneOf([Yup.ref("password")], "Passwords must match")
    .required("Required"),
});

const SupplierAccountSetupPage = () => {
  const isMounted = useMounted();
  const router = useRouter();
  const searchParams = useSearchParams();
  const username = searchParams.get("username") || undefined;
  const params = useParams();
  const navigate = useNavigate();

  const { showAlert } = useContext(AlertContext);

  // Function to send the token to the backend API
  const verifyTokenValidity = async () => {
    const token = params?.token;

    if (!token) navigate("/404", { replace: true });

    const result = await axios
      .get(`${API}/api/pa-supplier/validate-supplier-setup/${token}`)
      .catch((error) => {
        navigate("/401", { replace: true });
      });

    if (result?.data?.status === 404) {
      navigate("/401", { replace: true });
    }

    if (result?.data?.email) {
      formik.setFieldValue("email", result.data.email);
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: getInitialValues(username),
    validationSchema,
    onSubmit: async (values, helpers) => {
      try {
        const token = params?.token;
        const firstname = values?.firstname;
        const lastname = values?.lastname;
        const email = values?.email;
        const password = values?.password;

        if (!password) return;

        // Call backend API
        const result = await axios.post(
          `${API}/api/pa-supplier/supplier-account-setup/${token}`,
          {
            firstname,
            lastname,
            email,
            password,
          }
        );

        if (isMounted()) {
          showAlert("Account setup successfully!", "success");
          router.push(paths.auth.jwt.login);
        }
      } catch (err) {
        const errorMessage =
          err?.response?.data?.error?.message || "Request failed.";
        console.error(errorMessage);

        if (isMounted()) {
          helpers.setStatus({ success: false });
          helpers.setErrors({ submit: errorMessage });
          helpers.setSubmitting(false);
        }
      }
    },
  });

  useEffect(() => {
    verifyTokenValidity();
  }, []);

  usePageView();

  return (
    <AuthLayout>
      <Seo title="Supplier Account Setup" />
      <div>
        <Card
          elevation={16}
          style={{
            borderRadius: 0,
            boxShadow: "none",
            border: "1px solid #E6E6E6",
            width: "600px",
            padding: "15px",
            paddingTop: "25px",
          }}
        >
          <CustomTypography
            message="Account Setup"
            title
            style={{
              paddingLeft: "10px",
            }}
          />
          <CardContent>
            <form noValidate onSubmit={formik.handleSubmit}>
              <Stack
                style={{
                  marginTop: "25px",
                  flexDirection: "row",
                  alignItems: "flex-start",
                  justifyContent: "space-around",
                }}
              >
                {/* First Column */}
                <Stack
                  direction="column"
                  spacing={3}
                  style={{
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                  }}
                >
                  <CustomTextField
                    touched={formik.touched.firstname}
                    errors={formik.errors.firstname}
                    fullWidth
                    helperText={
                      formik.touched.firstname && formik.errors.firstname
                    }
                    label="First Name"
                    name="firstname"
                    onBlur={formik.handleBlur}
                    handleChange={formik.handleChange}
                    value={formik.values.firstname}
                  />
                  <CustomTextField
                    touched={formik.touched.lastname}
                    errors={formik.errors.lastname}
                    fullWidth
                    helperText={
                      formik.touched.lastname && formik.errors.lastname
                    }
                    label="Last Name"
                    name="lastname"
                    onBlur={formik.handleBlur}
                    handleChange={formik.handleChange}
                    value={formik.values.lastname}
                  />
                  <CustomTextField
                    touched={formik.touched.email}
                    errors={formik.errors.email}
                    fullWidth
                    helperText={formik.touched.email && formik.errors.email}
                    label="Email"
                    name="email"
                    onBlur={formik.handleBlur}
                    handleChange={formik.handleChange}
                    value={formik.values.email}
                    style={{ width: "250px" }}
                  />
                </Stack>

                {/* Second Column */}
                <Stack
                  direction="column"
                  spacing={3}
                  style={{
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                  }}
                >
                  <CustomTextField
                    touched={formik.touched.password}
                    errors={formik.errors.password}
                    fullWidth
                    helperText={
                      formik.touched.password && formik.errors.password
                    }
                    label="Password"
                    name="password"
                    onBlur={formik.handleBlur}
                    handleChange={formik.handleChange}
                    type="password"
                    value={formik.values.password}
                  />
                  <CustomTextField
                    touched={formik.touched.passwordConfirm}
                    errors={formik.errors.passwordConfirm}
                    fullWidth
                    helperText={
                      formik.touched.passwordConfirm &&
                      formik.errors.passwordConfirm
                    }
                    label="Password Confirmation"
                    name="passwordConfirm"
                    onBlur={formik.handleBlur}
                    handleChange={formik.handleChange}
                    type="password"
                    value={formik.values.passwordConfirm}
                  />
                </Stack>
              </Stack>
              {formik.errors.submit && (
                <FormHelperText error sx={{ mt: 3 }}>
                  {formik.errors.submit}
                </FormHelperText>
              )}
              <Stack style={{ marginTop: "50px" }}>
                <CustomButton
                  label="Submit"
                  disabled={formik.isSubmitting}
                  fullWidth
                  fullSize
                  type="submit"
                  variant="contained"
                />
              </Stack>
            </form>
          </CardContent>
        </Card>
      </div>
    </AuthLayout>
  );
};

export default SupplierAccountSetupPage;
